// AI Agents Page - Specialized PFMEA Analysis Agents
// ============================================================

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    Bot,
    Send,
    RefreshCw,
    Sparkles,
    AlertTriangle,
    Shield,
    FileText,
    Activity,
    CheckCircle
} from 'lucide-react';
import clsx from 'clsx';
import { PFMEA_AI_AGENTS, PfmeaAgentType } from '../constants/pfmeaAgents';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

const AiAgentsPage: React.FC = () => {
    const { standardConfig, activeProjectName } = useAppContext();
    const { hasPermission } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [searchParams] = useSearchParams();

    // State
    const [selectedAgent, setSelectedAgent] = useState<PfmeaAgentType>(() => {
        const agentParam = searchParams.get('agent');
        if (agentParam && Object.keys(PFMEA_AI_AGENTS).includes(agentParam)) {
            return agentParam as PfmeaAgentType;
        }
        return 'process-failure-analysis';
    });

    // Update selected agent if URL changes
    useEffect(() => {
        const agentParam = searchParams.get('agent');
        if (agentParam && Object.keys(PFMEA_AI_AGENTS).includes(agentParam)) {
            setSelectedAgent(agentParam as PfmeaAgentType);
            setChatHistory([]); // Clear chat history when switching via URL
        }
    }, [searchParams]);

    const [userMessage, setUserMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'assistant', content: string }[]>([]);

    const currentAgent = PFMEA_AI_AGENTS[selectedAgent];

    const handleSendMessage = async () => {
        if (!userMessage.trim()) return;

        // Add user message to history
        const newHistory = [...chatHistory, { role: 'user' as const, content: userMessage }];
        setChatHistory(newHistory);
        setUserMessage('');
        setIsGenerating(true);

        try {
            // Build agent-specific prompt
            const agentPrompt = `${currentAgent.systemPrompt}

User Query: ${userMessage}

Context:
- Project: ${activeProjectName || 'Unknown'}
- Agent Type: ${selectedAgent}
- Standard: ${standardConfig?.name || 'AIAG-VDA'}

Provide a helpful response following AIAG-VDA PFMEA guidelines.`;

            // Call backend workflow coach endpoint using authenticated utility
            const result = await authenticatedFetchJSON<any>('/api/agents/workflow/coach', {
                method: 'POST',
                body: JSON.stringify({ prompt: agentPrompt }),
            });

            // Extract text from response (may be string or object)
            let assistantMessage = typeof result === 'string'
                ? result
                : result.response || result.content || result.message || JSON.stringify(result);

            setChatHistory([
                ...newHistory,
                { role: 'assistant', content: assistantMessage }
            ]);
        } catch (error) {
            console.error('Chat error:', error);
            setChatHistory([
                ...newHistory,
                { role: 'assistant', content: 'I encountered an error connecting to the AI service. Please try again or check your connection to the Magnus backend.' }
            ]);
        } finally {
            setIsGenerating(false);
        }
    };

    if (!hasPermission('ai.generate_pfmea')) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <Bot className="mx-auto text-slate-400 mb-4" size={48} />
                    <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        AI Agents Not Available
                    </h2>
                    <p className="text-slate-500">
                        You need Engineer or higher role to use AI features.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 h-[calc(100vh-140px)] flex flex-col">
            {/* Header */}
            <div>
                <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                    <Bot className="text-purple-600" />
                    AI Specialized Agents
                </h1>
                <p className="text-slate-500 text-sm">
                    Select a specialized agent to help with specific PFMEA tasks ({standardConfig?.name || 'AIAG-VDA'})
                </p>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Agent Selection List */}
                <div className={clsx('rounded-xl border p-4 overflow-y-auto', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <h3 className={clsx('font-semibold mb-4 text-sm uppercase tracking-wider', isDark ? 'text-slate-400' : 'text-slate-500')}>
                        Available Agents
                    </h3>
                    <div className="space-y-2">
                        {(Object.keys(PFMEA_AI_AGENTS) as PfmeaAgentType[]).map((agentId) => {
                            const agent = PFMEA_AI_AGENTS[agentId];
                            const isSelected = selectedAgent === agentId;

                            // Map icon string to Lucide component
                            const IconComponent =
                                agent.icon === 'AlertTriangle' ? AlertTriangle :
                                    agent.icon === 'Shield' ? Shield :
                                        agent.icon === 'FileText' ? FileText :
                                            agent.icon === 'Activity' ? Activity :
                                                agent.icon === 'CheckCircle' ? CheckCircle : Bot;

                            return (
                                <button
                                    key={agentId}
                                    onClick={() => setSelectedAgent(agentId)}
                                    className={clsx(
                                        'w-full text-left p-3 rounded-lg transition-all border',
                                        isSelected
                                            ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300 dark:bg-blue-900/30 dark:border-blue-700'
                                            : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-700'
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={clsx(
                                            'p-2 rounded-lg',
                                            isSelected ? 'bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-200' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                                        )}>
                                            <IconComponent size={18} />
                                        </div>
                                        <div>
                                            <p className={clsx('font-medium text-sm', isSelected ? 'text-blue-700 dark:text-blue-300' : isDark ? 'text-white' : 'text-slate-800')}>
                                                {agent.name}
                                            </p>
                                            <p className={clsx('text-xs mt-0.5 line-clamp-2', isSelected ? 'text-blue-600/80 dark:text-blue-300/80' : 'text-slate-500')}>
                                                {agent.description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Chat Interface */}
                <div className={clsx('lg:col-span-3 rounded-xl border flex flex-col', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-purple-600 dark:text-purple-300">
                            <Bot size={20} />
                        </div>
                        <div>
                            <h3 className={clsx('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                                {currentAgent.name}
                            </h3>
                            <p className="text-xs text-slate-500">
                                {currentAgent.description}
                            </p>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {chatHistory.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-60">
                                <Sparkles size={48} className="text-blue-400 mb-4" />
                                <h4 className={clsx('text-lg font-medium mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                                    {currentAgent.name} Ready
                                </h4>
                                <p className="text-sm text-slate-500 max-w-md">
                                    Ask me anything about {currentAgent.name.toLowerCase()}. I can help analyze specific problems, check compliance, or suggest improvements.
                                </p>
                            </div>
                        ) : (
                            chatHistory.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={clsx(
                                        'flex gap-3 max-w-[85%]',
                                        msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''
                                    )}
                                >
                                    <div className={clsx(
                                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                                        msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'
                                    )}>
                                        {msg.role === 'user' ? 'U' : <Bot size={16} />}
                                    </div>
                                    <div className={clsx(
                                        'p-3 rounded-2xl text-sm whitespace-pre-wrap',
                                        msg.role === 'user'
                                            ? 'bg-blue-600 text-white rounded-tr-none'
                                            : isDark ? 'bg-slate-700 text-slate-200 rounded-tl-none' : 'bg-slate-100 text-slate-800 rounded-tl-none'
                                    )}>
                                        {msg.content}
                                    </div>
                                </div>
                            ))
                        )}
                        {isGenerating && (
                            <div className="flex gap-3 max-w-[85%]">
                                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                                    <Bot size={16} className="text-white animate-pulse" />
                                </div>
                                <div className={clsx(
                                    'p-3 rounded-2xl text-sm bg-slate-100 dark:bg-slate-700 rounded-tl-none flex items-center gap-2',
                                    isDark ? 'text-slate-300' : 'text-slate-600'
                                )}>
                                    <RefreshCw size={14} className="animate-spin" />
                                    Analyzing...
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Input Area */}
                    <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={userMessage}
                                onChange={(e) => setUserMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                placeholder={`Ask ${currentAgent.name} a question...`}
                                className={clsx(
                                    'flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-600',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-800'
                                )}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={!userMessage.trim() || isGenerating}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiAgentsPage;
